import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logEvent } from "@/hooks/use-event-log";

async function invokeEdgeFunction<T = unknown>(
  name: string,
  body?: Record<string, unknown>,
  params?: Record<string, string>,
): Promise<T> {
  const url = new URL(`${SUPABASE_URL}/functions/v1/${name}`);
  if (params) {
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  }

  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(url.toString(), {
    method: body ? "POST" : "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Edge function ${name} failed (${res.status}): ${text}`);
  }

  return res.json();
}

/** Approve or reject a voucher, linking to a budget line */
export function useApproveVoucher() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      voucher_id: string;
      decision: "approved" | "rejected";
      budget_line_id?: string | null;
      comment?: string;
      user_id: string;
      /** Projektet bilaget tilhører — brukes til målrettet invalidering. */
      project_id?: string;
    }) => {
      // Stripp felter som ikke skal til edge-funksjonen:
      // - project_id (VIB-649): edge-funksjonen bruker det ikke
      // - user_id: edge-funksjonen utleder caller fra JWT, ikke body
      const { project_id: _project_id, user_id: _user_id, ...payload } = input;
      void _project_id;
      void _user_id;
      return invokeEdgeFunction<{
        success: boolean;
        new_status?: string;
      }>("approve-voucher", payload);
    },
    onSuccess: async (data, variables) => {
      await invalidateFor(qc, "vouchers", { projectId: variables.project_id });
      const message = variables.decision === "approved"
        ? data?.new_status === "forwarded_to_dl"
          ? "Bilag godkjent og sendt til daglig leder"
          : "Bilag godkjent"
        : "Bilag avvist";
      toast.success(message);
      // VIB-1184 D15: voucher_approved/voucher_rejected logges autoritativt av
      // DB-triggeren log_voucher_approval (project_events). Frontend-duppen
      // fjernet. (voucher.synced / approval_synced beholdes — sync-ops, ikke dup.)
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

/** Trigger a sync of vouchers from PowerOffice */
export function useSyncVouchers() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return invokeEdgeFunction("sync-poweroffice-vouchers");
    },
    onSuccess: async () => {
      // Bred invalidering: sync kan oppdatere bilag på vilkårlige prosjekter,
      // så det er ikke mulig å målrette én projectId her (VIB-649).
      await invalidateFor(qc, "vouchers");
      toast.success("Bilag synkronisert");
      logEvent({
        event_type: "voucher.synced",
        source: "sync",
      });
    },
    onError: () => {
      toast.error("Noe gikk galt");
    },
  });
}

/**
 * Trigger sync av VoucherApproval-køen (bilag som er sendt til Sølvrev for
 * første-linje godkjenning). Default er prod; sett `env: "demo"` for
 * verifisering mot demo-klienten før prod-rollout.
 */
export function useSyncVoucherApprovals() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input?: { env?: "prod" | "demo" }) => {
      return invokeEdgeFunction<{
        success: boolean;
        env: string;
        queue_size: number;
        upserted: number;
        auto_forwarded: number;
        skipped: number;
        errors: Array<{ voucher_id: string; message: string }>;
      }>("sync-poweroffice-voucher-approvals", input ?? {});
    },
    onSuccess: async (data) => {
      // Bred invalidering: sync kan oppdatere bilag på vilkårlige prosjekter,
      // så det er ikke mulig å målrette én projectId her (VIB-649).
      await invalidateFor(qc, "vouchers");
      const detail = data?.upserted
        ? `${data.upserted} til godkjenning`
        : data?.auto_forwarded
        ? `${data.auto_forwarded} auto-videresendt`
        : "ingen nye bilag";
      toast.success(`Synk fullført (${data?.env ?? "prod"}): ${detail}`);
      logEvent({
        event_type: "voucher.approval_synced",
        source: "sync",
      });
    },
    onError: (err) => {
      toast.error(`Synk feilet: ${(err as Error).message}`);
    },
  });
}

/**
 * Knytt et bilag til en budgetlinje. Skriver til vouchers.budget_line_id
 * direkte (tidligere brukte vi voucher_approvals.budget_line_id, men det
 * krevde at en approval-rad eksisterte — som den ikke gjorde pga
 * log-trigger-bugen, så UI viste falsk success-toast uten at noe ble
 * lagret).
 */
export function useUpdateVoucherBudgetLine() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ voucherId, budgetLineId }: { voucherId: string; budgetLineId: string | null; projectId?: string }) => {
      const { error } = await supabase
        .from("vouchers")
        .update({ budget_line_id: budgetLineId, updated_at: new Date().toISOString() })
        .eq("id", voucherId);
      if (error) throw error;
    },
    onSuccess: async (_data, variables) => {
      // VIB-649: målrett invalidering når projectId er kjent.
      await invalidateFor(qc, "vouchers", { projectId: variables.projectId });
      toast.success("Budsjettlinje oppdatert");
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere budsjettlinje: ${(err as Error).message}`);
    },
  });
}

/** Fetch voucher document as blob URL (with proper auth headers) */
export async function fetchVoucherDocumentBlob(
  voucherId: string,
  format: "pdf" | "images" | "ehf" | "all" = "pdf",
): Promise<{ blobUrl: string; contentType: string; blob: Blob }> {
  const url = `${SUPABASE_URL}/functions/v1/get-voucher-document?voucher_id=${voucherId}&format=${format}`;

  const { data: { session } } = await supabase.auth.getSession();

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: SUPABASE_ANON_KEY,
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Kunne ikke hente dokument (${res.status}): ${text}`);
  }

  const contentType = res.headers.get("content-type") ?? "application/pdf";
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  return { blobUrl, contentType, blob };
}
